import { h } from 'vue'
import ccConfig from './feed-comment-capture.config'
import { createAndMountApp } from 'util/loader-helper'
import domUtils from 'util/dom-utils'
import createTransactionService from 'services/api/create-transaction'
import createTransactionAndGetItemsForCommentService
  from 'services/api/create-transaction-and-get-items-for-comment'
import {
  CreateTransactionBody,
  RestError,
  TransactionItemsForCommentResponse
} from 'services/api-service'
import { isPositiveInteger, isValidCurrency, isValidPrice } from 'util/validation-utils'
import logger from 'util/logger'
import Modal from 'objects/modal/Modal.vue'
import msg from 'services/msg'
import trackingService from 'services/tracking-service'

function loadCommentCapture(data: TransactionItemsForCommentResponse) {
  if  (data.items.length === 0) {
    return
  }
  // eslint-disable-next-line
  import(/* webpackChunkName: 'cc-widget' */ './CommentCapture.vue').then((CommentCaptureWidget) => {
    const props = { items: data.items, siteUser: data.siteUser, token: data.token }
    if (ccConfig.embed) {
      createAndMountApp({
        render: () => h(CommentCaptureWidget.default, props)
      }, ccConfig.widgetDivId)
    } else {
      // open in modal
      const divId = ccConfig.widgetDivId
      domUtils.appendDivToBody(divId)
      // if the client specified a delay before showing, it will wait, otherwise, timeout is 0 and
      // will mount right away
      setTimeout(() => {
        createAndMountApp({
          data() { return { showModal: true } },
          render() {
            if (this.showModal) {
              return h(Modal,
                {
                  id: divId,
                  class: 'tt-c-comment-capture-modal',
                  onClose: () => {
                    this.showModal = false
                  },
                  // props
                  ariaLabel: msg('cc.modal.title'),
                  ariaDescribedById: 'tt-comment-capture-desc'
                },
                [h(CommentCaptureWidget.default, props)]
              )
            }
          }
        }, ccConfig.widgetDivId)
      }, (ccConfig.delayDisplaySeconds * 1000))
    }
  })
}

function valid(order: CreateTransactionBody) {
  let valid = true;
  ['orderId', 'firstName', 'lastName'].forEach((field) => {
    if (!order[field]) {
      logger.error(`order feed - ${field} is required`)
      valid = false
    }
  })
  if (!(order.email || order.externalUserId)) {
    logger.error('order feed - email or externalUserId is required')
    valid = false
  }
  if (!!order.total && !isValidPrice(order.total)) {
    logger.error('order feed - "total" is not a valid value')
    valid = false
  }
  if (!!order.currency && !isValidCurrency(order.currency)) {
    logger.error('order feed - "currency" is not a valid value. Must be a valid ISO 4217 code')
    valid = false
  }
  if (!order.items || order.items.length === 0) {
    logger.error('order feed - at least one line item is required')
    valid = false
  } else {
    order.items.forEach((item) => {
      if (!item.sku || !item.sku.toString().trim()) {
        logger.error('order feed - sku is required for each line item')
        valid = false
      }
      if (!!item.qty && !isPositiveInteger(item.qty)) {
        logger.error('order feed = "qty" is not a valid integer')
        valid = false
      }
      if (!!item.price && !isValidPrice(item.price)) {
        logger.error('order feed - "price" is not a valid value')
        valid = false
      }
    })
  }
  return valid
}

export function send(order: CreateTransactionBody): void {
  if (valid(order)) {
    const turnToConfig = window.turnToConfig || {}
    const ccTurnToConfig = turnToConfig.commentCapture || {}
    order.regions = turnToConfig.regions
    if (ccConfig.enabled && !ccTurnToConfig.suppress) {
      createTransactionAndGetItemsForCommentService.createTransactionAndGetItemsForComment(order)
        .then((data: TransactionItemsForCommentResponse) => {
          loadCommentCapture(data)
        }).catch((e: RestError) => {
          logger.error('Error sending order', e)
        })
    } else {
      // no comment capture
      createTransactionService.createTransaction(order)
        .catch((e: RestError) => logger.error('Error sending order', e))
    }
    sendOrderEvent(order)
  }
}

function sendOrderEvent(order: CreateTransactionBody): void {
  trackingService.queueEvent('feed-comment-capture', 'order', null, {
    oId: order.orderId,
    c: order.currency,
    t: order.total,
    i: order.items?.map(item => ({
      sku: item.sku,
      p: item.price,
      q: item.qty
    }))
  })
}
